import React from "react";
import { getCurrencySymbol } from "../../../../../tenant/currency";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "./ask-a-friend-form.module.scss";
import Button from "../../../../../common/ekedc-components/button/Button";
import { CardFrame } from "../../../../../common/components/card-frame/CardFrame";
import { PhoneInput } from "../../../components";
import { FriendRequestNotificationChannel, InitiateFriendPaymentRequestPayload } from "../../../../../model/charge";
import {
	useInitiatePostpaidFriendPaymentRequestMutation,
	useInitiatePrepaidFriendPaymentRequestMutation,
} from "../../../../../service/vending-service";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { validatePhoneNumber } from "../../../validators";
import { useTenant } from "../../../../../tenant/branding/useTenant";

interface AskAFriendFormProps {
	utilityId: string;
	meterNumber: string;
	amount: string;
	currency: string;
	paymentType: "prepaid" | "postpaid";
	onSuccess: (requestLink: string, recipientEmail: string) => void;
}

interface AskAFriendFormValues {
	notificationChannel: FriendRequestNotificationChannel;
	recipientEmail: string;
	recipientPhone: string;
	note: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const sym = (currency: string) => getCurrencySymbol(currency);

export const AskAFriendForm: React.FC<AskAFriendFormProps> = ({
	utilityId,
	meterNumber,
	amount,
	currency,
	paymentType,
	onSuccess,
}) => {
	const { t } = useTranslation();
	const { countryCode } = useTenant();
	const [initiatePrepaidFriendRequest, { isLoading: isSendingPrepaid }] = useInitiatePrepaidFriendPaymentRequestMutation();
	const [initiatePostpaidFriendRequest, { isLoading: isSendingPostpaid }] = useInitiatePostpaidFriendPaymentRequestMutation();
	const isSending = isSendingPrepaid || isSendingPostpaid;

	const {
		control,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<AskAFriendFormValues>({
		defaultValues: {
			notificationChannel: FriendRequestNotificationChannel.Email,
			recipientEmail: "",
			recipientPhone: "",
			note: "",
		},
	});

	const notificationChannel = watch("notificationChannel");
	const needsPhone = notificationChannel !== FriendRequestNotificationChannel.Email;

	const onSubmit = async (data: AskAFriendFormValues) => {
		const payload: InitiateFriendPaymentRequestPayload = {
			utilityId,
			meterNumber,
			amount: Number.parseFloat(amount),
			returnUrl: `${window.location.origin}/buy-electricity`,
			paymentTypes: ["Card"],
			recipientEmail: data.recipientEmail.trim(),
			recipientPhoneNumber: needsPhone ? data.recipientPhone : undefined,
			note: data.note.trim() || undefined,
			notificationChannel: data.notificationChannel,
		};

		try {
			const response =
				paymentType === "prepaid"
					? await initiatePrepaidFriendRequest(payload).unwrap()
					: await initiatePostpaidFriendRequest(payload).unwrap();
			onSuccess(response.requestLink, payload.recipientEmail);
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data) ?? t("payment.paymentFailed"));
		}
	};

	return (
		<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.scrollContent}>
				<div className={styles.recapCard}>
					<div className={styles.recapRow}>
						<span className={styles.recapLabel}>{t("common.labels.meterType")}</span>
						<span className={styles.recapValue}>{paymentType === "prepaid" ? t("common.labels.prepaid") : t("common.labels.postpaid")}</span>
					</div>
					<div className={styles.recapRow}>
						<span className={styles.recapLabel}>{t("common.labels.meterNumber")}</span>
						<span className={styles.recapValue}>{meterNumber}</span>
					</div>
					<div className={styles.recapRow}>
						<span className={styles.recapLabel}>{t("common.labels.amount")}</span>
						<span className={styles.recapValue}>{sym(currency)}{Number.parseFloat(amount || "0").toLocaleString()}</span>
					</div>
				</div>

				<CardFrame headerSectionTitle={t("payment.selectNotificationType")} paddingBottom={0}>
					<Controller
						name="notificationChannel"
						control={control}
						render={({ field }) => (
							<div className={styles.channelOptions}>
								{[
									{ value: FriendRequestNotificationChannel.Email, label: t("payment.notificationChannelEmail") },
									{ value: FriendRequestNotificationChannel.Sms, label: t("payment.notificationChannelPhone") },
									{ value: FriendRequestNotificationChannel.Both, label: t("payment.notificationChannelBoth") },
								].map(option => {
									const isSelected = field.value === option.value;
									return (
										<button
											key={option.value}
											type="button"
											className={`${styles.channelOption} ${isSelected ? styles.channelOptionSelected : ""}`}
											onClick={() => field.onChange(option.value)}
										>
											<span className={styles.channelOptionLabel}>{option.label}</span>
											<span className={`${styles.radio} ${isSelected ? styles.radioSelected : ""}`}>
												{isSelected && <span className={styles.radioDot} />}
											</span>
										</button>
									);
								})}
							</div>
						)}
					/>
				</CardFrame>

				{needsPhone && (
					<CardFrame headerSectionTitle={t("payment.recipientPhone")} overflowVisible>
						<div className={styles.cardBody}>
							<Controller
								name="recipientPhone"
								control={control}
								rules={{
									required: t("payment.recipientPhoneRequired"),
									validate: validatePhoneNumber,
								}}
								render={({ field }) => (
									<PhoneInput
										value={field.value}
										onChange={phone => field.onChange(phone)}
										defaultCountry={countryCode}
										lockCountry
										placeholder={t("payment.recipientPhonePlaceholder")}
										error={errors.recipientPhone?.message}
										className={styles.compactPhoneInput}
									/>
								)}
							/>
						</div>
					</CardFrame>
				)}

				<CardFrame headerSectionTitle={t("payment.recipientEmail")}>
					<div className={styles.cardBody}>
						<Controller
							name="recipientEmail"
							control={control}
							rules={{
								required: t("common.validation.fieldIsRequired"),
								pattern: { value: EMAIL_PATTERN, message: t("payment.invalidEmailAddress") },
							}}
							render={({ field }) => (
								<input
									type="email"
									className={styles.textInput}
									placeholder={t("payment.recipientEmailPlaceholder")}
									value={field.value}
									onChange={field.onChange}
								/>
							)}
						/>
						{errors.recipientEmail && <p className={styles.errorText}>{errors.recipientEmail.message}</p>}
					</div>
				</CardFrame>

				<CardFrame headerSectionTitle={t("payment.noteOptionalMax110")}>
					<div className={styles.cardBody}>
						<Controller
							name="note"
							control={control}
							rules={{ maxLength: { value: 110, message: t("payment.noteTooLong") } }}
							render={({ field }) => (
								<textarea
									className={styles.textArea}
									placeholder={t("payment.notePlaceholder")}
									maxLength={110}
									value={field.value}
									onChange={field.onChange}
								/>
							)}
						/>
						{errors.note && <p className={styles.errorText}>{errors.note.message}</p>}
					</div>
				</CardFrame>

				<div className={styles.validityNote}>{t("payment.hardcoded_validityDescription")}</div>
			</div>

			<footer className={styles.footer}>
				<Button type="submit" text={t("payment.sendRequest")} loading={isSending} disabled={isSending} />
			</footer>
		</form>
	);
};

export default AskAFriendForm;
